import { cloneDeep } from 'lodash';

import {
    ButtonActionTypes, ImageTypes, PromotionTypes, SCHEMA_VERSION_SUPPORTED, TriggerTypes
} from 'constants/QuickPromotionConstants';
// @ts-ignore
import QuickPromotionAction from 'entities/QuickPromotionAction';
import apiRequest from 'lib/apiRequest';
import Entity from 'lib/Entity';

class QuickPromotion extends Entity {
  initialize() {
    this.urlRoot = '/quick_promotions';
  }

  getPromotion(): string {
    return this.get('promotion');
  }

  getSchemaVersion(): number {
    return this.get('schema_version');
  }

  getEndDate(): Date | undefined {
    return this.get('end_date');
  }

  getType(): number {
    return this.get('type');
  }

  getTrigger(): number {
    return this.get('trigger');
  }

  getHeader(): string | undefined {
    return this.get('header');
  }

  getBody(): string | undefined {
    return this.get('body');
  }

  getImageUrl(): string | undefined {
    return this.get('image_url');
  }

  getImageType(): number {
    return this.get('image_type');
  }

  getPrimaryAction(): QuickPromotionAction {
    return this.get('primary_action');
  }

  getSecondaryAction(): QuickPromotionAction | undefined {
    return this.get('secondary_action');
  }

  getData(): Object {
    return this.get('data');
  }

  parse(response: any) {
    const promotion = cloneDeep(response);
    promotion['primary_action'] = QuickPromotionAction.fromJSON(promotion['primary_action']);
    if (promotion['secondary_action']) {
      promotion['secondary_action'] = QuickPromotionAction.fromJSON(promotion['secondary_action']);
    }
    if (promotion['end_date']) {
      promotion['end_date'] = new Date(promotion['end_date']);
    }
    promotion['trigger'] = TriggerTypes[promotion['trigger'].toUpperCase()] || TriggerTypes.UNKNOWN;
    promotion['type'] = PromotionTypes[promotion['type'].toUpperCase()] || PromotionTypes.UNKNOWN;
    promotion['image_type'] =
      ImageTypes[promotion['image_type'].toUpperCase()] || ImageTypes.UNKNOWN;
    return promotion;
  }

  // Whether we understand how to show this QP
  isShowable(): boolean {
    // If it's an old schema, don't show it
    if (this.getSchemaVersion() > SCHEMA_VERSION_SUPPORTED) {
      return false;
    }

    // If the QPs already expired, don't show it
    const endDate = this.getEndDate();
    if (endDate != null && new Date() > endDate) {
      return false;
    }

    // If we don't know what type of QP it is, don't show it
    if (this.getType() === PromotionTypes.UNKNOWN) {
      return false;
    }

    // If we don't know what type of QP it is, don't show it
    if (this.getTrigger() === TriggerTypes.UNKNOWN) {
      return false;
    }

    // If we don't know how to display its image, don't show it
    if (this.getImageType() === ImageTypes.UNKNOWN) {
      return false;
    }

    // If we don't know how to handle one of the requested action types, don't show it
    if (
      this.getPrimaryAction() != null &&
      this.getPrimaryAction().getActionType() === ButtonActionTypes.UNKNOWN
    ) {
      return false;
    }

    const secondaryAction = this.getSecondaryAction();
    if (secondaryAction != null && secondaryAction.getActionType() === ButtonActionTypes.UNKNOWN) {
      return false;
    }

    // all checks passed, we should be able to show it!
    return true;
  }

  isDismissable() {
    return this.get('dismissable');
  }

  recordDismissal(response: string) {
    // $FlowIssue
    return apiRequest(`${this.url}`, 'POST', {
      promotion: this.getPromotion(),
      response,
    });
  }
}

export default QuickPromotion;
